import { Box, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';

import { ContactManagerLayout } from '@modules/ContactManager/components/Tabs/shared/ContactManagerLayout';
import { DealCalculator } from '@modules/ContactManager/components/Tabs/shared/DealCalculator/DealCalculator';
import {
  selectLastUpdateProductsPlanDate,
  selectLenderCode,
  selectWorkflowNextStep,
  setDealReCalculate,
  setLastCommentsRefresh,
} from '@store/reducers/contactManagerSlice';

import { queryParams, randomNumber } from '@shared/commonFunctions';
import { FormWithPrompt } from '@shared/components/FormWithPrompt';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { SERVICES } from '@shared/constants/apiConfig';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { Modal } from '@shared/components/modals/Modal';
import { useQueryState } from '@shared/hooks/useQueryState';
import { IProductCancellations } from '@modules/accounting/CheckerMenu/DealsQueue/ImportProductCancel';
import { UnIdentifiedDealsModal } from '@modules/accounting/CheckerMenu/DealsQueue/UnIdentifiedDealsModal';
import { selectIsDealStructureChanged, setIsDealStructureChanged } from '@store/reducers/dealSlice';
import { IDealCalculatorRequest } from '../../shared/DealCalculator/interfaces';
import { ContactFiles } from '../ContactFiles/ContactFiles';
import { CoBorrower } from './components/CoBorrower/CoBorrower';
import { Conditions } from './components/Conditions';
import { DealBuilder } from './components/DealBuilder';
import { DealSheets } from './components/DealSheets';
import { DealsTable } from './components/DealsTable';
import { LenderSelector } from './components/LenderSelector';
import { LoanToValue } from './components/LoanToValue';
import { Products } from './components/Products/Products';
import { Tiles } from './components/Tiles/Tiles';
import { Valuations } from './components/Valuations';
import { DealContext } from './DealContext/DealContext';
import { DealUserTypeEnum, DealsProps, IDeal, IDealFee, IDealSummary, IProductsFetch, orderedDealDropdown } from './interfaces';
import { requestDealToggle, requestDeleteDeal, requestUpdateDeal } from './queries';
import { dealValidationSchema } from './validation';

export const Deals = ({ deals, userCurrentAddress: address, otherIncomes, selectLeadId, reloadLeadData, reloadDeals, validatingDeals, dealsSummaryError, reloadContactAlerts}: DealsProps) => {
  const [, setAssetIdParam] = useQueryState('assetId');
  const [dealIdParam, setDealIdParam] = useQueryState('dealId');
  const isDealSturctureChanged = useSelector(selectIsDealStructureChanged(dealIdParam));
  const [productCancellationCheckId, setProductCancellationCheckId] = useQueryState('productCancellationCheckId');
  const [unIdentifiedDealModalPosition, setUnIdentifiedDealModalPosition] = useState(0);

  const lenderCode = useSelector(selectLenderCode);
  const workflowDetailsNextStep = useSelector(selectWorkflowNextStep);
  const lastUpdateProductsPlanDate = useSelector(selectLastUpdateProductsPlanDate);
  const dispatch = useDispatch();
  const params = queryParams();

  const [reCalculate, setReCalculate] = useState(false);
  const [reCalculationInProgress, setReCalculationInProgress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDealBuilder, setShowDealBuilder] = useState(false);
  const [contextValue, setContextValue] = useState<any>({});
  const [isVoidEnable, setIsVoidEnable] = useState(false);
  const [dealCalculatorToOpen, setDealCalculatorToOpen] = useState<Partial<IDealCalculatorRequest> | null>(null);

  const { data: productCancellations = [] } = useSWR<IProductCancellations[]>(
    productCancellationCheckId && `${SERVICES.ACCOUNTING}/productcancellations/checks?displayChecksValue=1`
  );

  const {
    data: deal,
    mutate: reloadDeal,
    isLoading: isDealLoading,
    isValidating,
    error: dealError = null,
  } = useSWR<IDeal>(dealIdParam && `/${SERVICES.DEAL}/deals/${dealIdParam}`, {
    revalidateOnMount: false,
    revalidateIfStale: true,
  });

  const {
    data: dealProducts,
    mutate: mutateDealProducts,
    isLoading,
  } = useSWR<IProductsFetch>(dealIdParam && `/${SERVICES.PRODUCT}/deals/${dealIdParam}/dealproducts/summary`, {revalidateOnMount: false});

  useEffect(() => {
    if (isDealSturctureChanged?.assetId) {
      setLoading(true);
      const filteredDeals = deals.filter((deal) => deal?.assetId === isDealSturctureChanged?.assetId);
      filteredDeals &&
        filteredDeals.map((deal) => {
          dispatch(
            setIsDealStructureChanged({
              dealId: deal?.dealId,
              data: {
                isDealStructureChanged: true,
                assetId: '',
              },
            })
          );
        });
      setLoading(false);
    }
  }, [isDealSturctureChanged, deals]);

  const dealProductLen = useMemo(() => {
    const prodStatus = 'Contracted';
    const contractedProdLen = (dealProducts?.dealsProducts || []).filter((prod) => prod?.status === prodStatus);
    return !!dealProducts?.dealsProducts.length && (contractedProdLen.length !== dealProducts?.dealsProducts.length);
  }, [dealProducts?.dealsProducts]);

  const showProductChangeModal = useMemo(() => {
    return dealIdParam && dealProductLen && isDealSturctureChanged?.isDealStructureChanged;
  }, [isDealSturctureChanged?.isDealStructureChanged, dealProductLen, dealIdParam]);

  const productCancellationCheck = useMemo(() => {
    if (productCancellationCheckId && productCancellations && productCancellations?.length > 0) {
      setUnIdentifiedDealModalPosition((prevState) => prevState + 1);
      return (
        productCancellations.find(
          (productCancellation) => productCancellation.productCancellationCheckId === productCancellationCheckId
        ) ?? null
      );
    }

    return null;
  }, [productCancellationCheckId, productCancellations]);

  const preventDealSave = useMemo(() => {
    const disAllowStepsArray = deal?.disallowWorkflowSteps ? deal.disallowWorkflowSteps.split(',') : [];
    const disAllowWorkflowIds = disAllowStepsArray.filter((step) => step.startsWith('W')).map((id) => id.slice(1));
    const disAllowStepIds = disAllowStepsArray.filter((step) => step.startsWith('S')).map((id) => id.slice(1));

    const currentWorkflowNumIds = deal?.currentSteps?.map((step) => String(step.workflowNumericId)) ?? [];
    const currentStepNumIds = deal?.currentSteps?.map((step) => String(step.workflowStepNumericId)) ?? [];

    const preventSave =
      disAllowWorkflowIds.some((id) => currentWorkflowNumIds.includes(id)) ||
      disAllowStepIds.some((id) => currentStepNumIds.includes(id));

    return preventSave;
  }, [deal?.currentSteps, deal?.disallowWorkflowSteps]);

  const initValues = useMemo((): IDeal => {
    type ContextType = Record<string, string>;
    const context: ContextType = {};

    orderedDealDropdown.forEach((dealUserType) => {
      const user = deal?.dealUsersResponses?.find((user) => user.dealUserType === dealUserType);
      context[DealUserTypeEnum[dealUserType]] = user?.userName || '';
    });
    setContextValue(context);

    const addionalFees = deal?.financeConditionItem?.dealFeeItems?.filter((f: IDealFee) => f.isAdditionalFee) || [];
    const dealFees = deal?.financeConditionItem?.dealFeeItems?.filter((f: IDealFee) => !f.isAdditionalFee) || [];
    return { ...deal, additionalFeeArray: addionalFees, dealFeeArray: dealFees } as IDeal;
  }, [deal, orderedDealDropdown]);

  useEffect(() => {
    const loading = (!dealError && !deal) || isValidating;
    dealIdParam && setLoading(loading);

    if (!isDealLoading) {
      if (deal) {
        deal?.id != dealIdParam && selectLeadId(deal.leadId);
        setIsVoidEnable(deal.isVoidEnable);
      }

      // wait for deal to update with new rate and term from CUDL and then call re-calculate
      if (reCalculate) {
        dispatch(setDealReCalculate(true));
        setReCalculate(false);
      }
    }
  }, [deal, isDealLoading, dealError, dealIdParam, reCalculate, isValidating]);

  useEffect(() => {
    reloadDeals?.();
    reloadDeal?.();
  }, [lenderCode, workflowDetailsNextStep, lastUpdateProductsPlanDate, params?.id]);

  const onReset = () => {
    // calculation is calling form reset with new calculated data but deal is not saved yet
    setReCalculationInProgress(true);
  };

  const handleSubmit = useCallback(
    async (data: IDeal) => {
      if (dealSavePreValidation()) {
        if (isCalculationNeeded() && !reCalculationInProgress) {
          setReCalculationInProgress(true);
          dispatch(setDealReCalculate(true));
        } else {
          setLoading(true);
          const dealFees =
            data.additionalFeeArray
              ?.map((ad: IDealFee) => {
                return { ...ad, isAdditionalFee: true };
              })
              .concat(data.dealFeeArray || []) || [];

          const postData: IDeal = {
            ...data,
            carrierTrackingNumberIn: randomNumber(100000, 900000) + '', // TODO: Remove once API is fixed
            carrierTrackingNumberOut: randomNumber(100000, 900000) + '', // TODO: Remove once API is fixed
            financeConditionItem: {
              ...data.financeConditionItem,
              dealFeeItems: dealFees,
            },
          };

          const response = await requestUpdateDeal(postData, params.id);
          await reloadDeals();
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
          setLoading(false);
          setReCalculationInProgress(false);
          return response;
        }
      }

      function isCalculationNeeded(): boolean {
        if (
          data?.financeConditionItem?.daysToFirstPayment != deal?.financeConditionItem?.daysToFirstPayment ||
          data?.financeConditionItem?.salesPrice != deal?.financeConditionItem?.salesPrice ||
          data?.financeConditionItem?.totalSalesTaxPercentage != deal?.financeConditionItem?.totalSalesTaxPercentage ||
          data?.financeConditionItem?.totalSalesTaxAmount != deal?.financeConditionItem?.totalSalesTaxAmount ||
          data?.financeConditionItem?.cashBackAmount != deal?.financeConditionItem?.cashBackAmount
        ) {
          return true;
        }
        return false;
      }
    },
    [deal, reCalculationInProgress, preventDealSave, initValues]
  );

  const dealSavePreValidation = useCallback(() => {
    if (preventDealSave) {
      Notistack.generalError('Sorry you can`t save in the current workflow step.');
      reloadDeal();
      // formik.resetForm?.({
      //   values: initValues,
      // });
      return false;
    }
    return true;
  }, [preventDealSave]);

  const handleDealCheck = async (dealId: string) => {
    setLoading(true);
    await requestDealToggle(dealId);
    await reloadDeals();
    setDealIdParam(dealId);
    reloadLeadData();
    setLoading(false);
  };

  const handleDealBuilder = (row: IDealSummary) => {
    const rowId = row.dealId;
    if (rowId) {
      setDealIdParam(rowId);
      selectLeadId(row?.leadId);
      setAssetIdParam(row?.assetId);
      setShowDealBuilder(true);
    }
  };

  const handleDealDelete = async (dealId: string) => {
    await requestDeleteDeal(dealId);
    setDealIdParam(undefined);
    setShowDealBuilder(false);
    await reloadDeals();
  };

  const handleCloseUnIdenifiedDealsModal = useCallback(() => {
    setProductCancellationCheckId('');
    setUnIdentifiedDealModalPosition(0);
  }, []);

  return (
    <DealContext.Provider value={{ contextValue, setContextValue }}>
      <ContactManagerLayout>
        {dealCalculatorToOpen && (
          <DealCalculator
            open
            data={dealCalculatorToOpen}
            assetNumericId={deal?.assetNumericId}
            numericId={deal?.numericId}
            isVoidEnable={deal?.isVoidEnable}
            refreshDeals={reloadDeals}
            reloadDeal={reloadDeal}
            contactAddress={address}
            handleClosePopUp={() => setDealCalculatorToOpen(null)}
            reloadContactAlerts={reloadContactAlerts}
          />
        )}

        {productCancellationCheck && (
          <UnIdentifiedDealsModal
            deals={deals}
            unIdentifiedDealModalPosition={unIdentifiedDealModalPosition}
            selectedDeal={productCancellationCheck}
            onClose={handleCloseUnIdenifiedDealsModal}
          />
        )}

        <LoaderBackdrop open={loading} />
        <DealsTable
          data={deals}
          dealId={dealIdParam}
          selectDealId={setDealIdParam}
          contactAddress={address}
          error={dealsSummaryError}
          validatingDeals={validatingDeals}
          setShowDealBuilder={setShowDealBuilder}
          handleDealToggle={handleDealCheck}
          deleteDeal={handleDealDelete}
          onSelect={handleDealBuilder}
          onCalculate={(data) => setDealCalculatorToOpen({ ...data, contactId: params.id })}
        />

        {showDealBuilder && (
          <Formik
            enableReinitialize
            initialValues={initValues}
            validationSchema={dealValidationSchema}
            onSubmit={handleSubmit}
            onReset={onReset}
          >
            {(props) => (
              <FormWithPrompt>
                <DealBuilder
                  data={props.values}
                  deals={deals}
                  setFieldValue={props.setFieldValue}
                  setFieldError={props.setFieldError}
                  refreshDealsTable={async () => {
                    await reloadDeals();
                  }}
                />

                <Grid
                  container
                  spacing={3}
                >
                  <Grid
                    item
                    xs={4}
                    xl={5}
                  >
                    <LenderSelector
                      dealResponse={deal}
                      setLoading={setLoading}
                      {...props}
                    />

                    <Valuations
                      data={props.values?.valuations}
                      lederDefaultValuation={deal?.lenderResponse?.generalInfo?.defaultValuationMethods}
                    />

                    <LoanToValue data={deal?.loanToValues} />
                  </Grid>

                  <Grid
                    item
                    xs={8}
                    xl={7}
                    sx={{ display: 'flex' }}
                  >
                    <Conditions
                      {...props}
                      deal={props.values}
                      reloadDeal={reloadDeal}
                      reloadDeals={reloadDeals}
                      isLoading={isDealLoading && !dealError}
                      dealSavePreValidation={dealSavePreValidation}
                      otherIncomes={otherIncomes}
                    />

                    <Tiles
                      deal={deal}
                      dealId={deal?.id || ''}
                      assetId={deal?.assetId || ''}
                      reloadDeal={reloadDeal}
                      dealData={dealProducts}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                  >
                    {dealIdParam && (
                      <Products
                        isVoidEnable={isVoidEnable}
                        contactAddress={address}
                        reloadDeal={reloadDeal}
                        dealProducts={dealProducts}
                        isLoading={isLoading}
                        deal={deal}
                        mutateDealProducts={mutateDealProducts}
                        preventDealSave={preventDealSave}
                      />
                    )}

                    {deal && (
                      <CoBorrower
                        contactId={params.id}
                        dealData={deal}
                        reloadDeal={reloadDeal}
                        refreshDealsTable={reloadDeals}
                      />
                    )}

                    {deal?.id && deal?.assetId && (
                      <ContactFiles
                        hideAssetDropDown
                        reloadDeal={reloadDeal}
                        dealIdParam={deal?.id}
                        assetIdParam={deal?.assetId}
                        dealSummary={deals}
                      />
                    )}
                    <DealSheets />
                  </Grid>
                </Grid>
              </FormWithPrompt>
            )}
          </Formik>
        )}

        {showProductChangeModal && (
          <Modal
            open
            dialogWidth="600px"
            title="Warning"
            onClose={() =>
              dispatch(
                setIsDealStructureChanged({
                  dealId: dealIdParam ?? '',
                  data: {
                    isDealStructureChanged: false,
                    assetId: '',
                  },
                })
              )
            }
          >
            <Box sx={{ padding: '20px' }}>
              <Typography>
                {' '}
                Deal Structure has changed. Please delete and re-add all products and verify any adjustments.{' '}
              </Typography>
            </Box>
          </Modal>
        )}
      </ContactManagerLayout>
    </DealContext.Provider>
  );
};
